/** @module csi/widget/_ComboBoxMenu */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/dom-style",
	"dijit/form/_ComboBoxMenu",
	"csi",
	"csi/html",
	"csi/locale",
	"csi/store/common",
	"csi/store/DelayedRequest",
	"csi/store/Names",
	"./ComboBoxPlaceholder",
	"./DelayFilteringPlaceholder"
], function(declare, domClass, domStyle, _ComboBoxMenu, csi, html, locale, {DISABLED_PROPERTY, INVALID_PROPERTY}, {DelayedRequestState}, Names, 
			ComboBoxPlaceholder, DelayFilteringPlaceholder) {

	/**
	 * Return true if given select option is disabled
	 * @param {DOMNode} node
	 * @return {boolean}
	 */
	function _isDisabledSelection(node) {
		return domClass.contains(node, html.CSS_DISABLED_SELECTION);
	}

	/**
	 * Return true if given select option is disabled or hidden
	 * @param {DOMNode} node
	 * @return {boolean}
	 */
	function _isDisabledNode(node) {
		return node && ((node.style.display == "none") || _isDisabledSelection(node));
	}

	return declare(_ComboBoxMenu, {
		/** @property {DelayedRequest} delayedStore - store indicating search results are delayed in which case placeholder is needed */
		delayedStore: null,

		/** @property {DelayedFilteringRequest} delayedFilteringStore - store indicating filtering results are delayed in which case placeholder is needed */
		delayedFilteringStore: null,

		/** @property {() => object} onPlaceholderParams - extension point for getting parameters to use for placeholder */
		onPlaceholderParams: null,

		/** @property {ComboBoxPlaceholder} placeholderNode - node containing instance of placeholder widget */
		placeholderNode: null,

		/** @property {string} popupWrappperCss - CSS class to apply to popup wrapper */
		popupWrappperCss: "",

		/** @property {Object} previewWidgetClass - class to use to construct preview widget to display more information about highlighted item */
		previewWidgetClass: null,

		/** @property {DOMNode} _previewNode - node containing instance of preview widget */
		_previewNode: null,

		/** @property {DOMNode[]} - list of menu actions menu items */
		// _actionsItems: null,

		/** @property {DOMNode}* - node containing sticky actions */
		_actionsNode: null,

		/** @property {() => boolean} isSelectedFunc - extension point for checking if an item is selected */
		isSelectedFunc: null,

		/**
		 * @override
		 * Destroy preview instance
		 */
		destroy: function(preserveDom) {
			const {delayedStore, placeholderNode, _previewNode, _actionsNode} = this;

			if (delayedStore) {
				delayedStore.onChangeState = null;
			}

			html.clear(placeholderNode, true);
			html.clear(_previewNode, true);
			html.clear(_actionsNode, true);
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Add custom class to menu items
		 */
		_createOption: function(item, labelFunc) {
			var menuitem = this.inherited(arguments);
			if (item[DISABLED_PROPERTY]) {
				domClass.add(menuitem, html.CSS_DISABLED_SELECTION);
			}

			if (item[INVALID_PROPERTY]) {
				domClass.add(menuitem, html.CSS_INVALID_SELECTION);
			}

			if (this.isSelectedFunc && this.isSelectedFunc(item)) {
				menuitem.classList.add('dijitMenuItemSelected');
			}

			if (item[Names.ACTION_PROPERTY]) {
				menuitem.classList.add((item[Names.ID_PROPERTY] == csi.DIALOG_URL) ? html.CSS_DROPDOWN_GOTODIALOG : html.CSS_DROPDOWN_ACTION);
				// initialize actions items array
				if (!this._actionsItems) {
					this._actionsItems = [];
				}

				// Place "Go to Dialog" at the top
				if (item[Names.ID_PROPERTY] == csi.DIALOG_URL) {
					this._actionsItems.unshift(menuitem);
				} else {
					this._actionsItems.push(menuitem);
				}
			}

			if (item[this.classAttr]) {
				domClass.add(menuitem, item[this.classAttr]);
			}

			if (menuitem.classList.contains("csi-grouped-selection-suggested")) {
				menuitem.innerHTML = `<span class="csi-grouped-selection-suggested-text">${menuitem.innerHTML}</span><span class="csi-grouped-selection-suggested-chip">${locale.resource("Suggested")}</span>`;
			}

			if (menuitem.classList.contains(html.CSS_INVALID_SELECTION)) {
				menuitem.innerHTML = `<span class="csi-invalid-selection-text">${menuitem.innerHTML}</span><span class="csi-invalid-selection-chip">${locale.resource("Invalid")}</span>`;
			}

			return menuitem;
		},

		/**
		 * @override
		 * Ignore disabled items, and handle paging
		 */
		onClick: function(node) {
			// do not do anything when disabled items are clicked
			if (!_isDisabledSelection(node)) {
				this.inherited(arguments);
			}

			// after user clicks on more/previous choices select appropriate next node
			if (!this.containSelection && (node == this.previousButton || node == this.nextButton)) {
				this.selectNextNode();
			}
		},

		/**
		 * @override
		 * Ignore disabled items, and show preview if so configured
		 */
		onHover: function(node) {
			if (!_isDisabledSelection(node)) {
				this.inherited(arguments);
			}
		},

		/**
		 * Extension point to select item on open
		 * @return {Object}
		 */
		onInitialSelection: function() {
			return null;
		},

		/**
		 * @override
		 * If preview widget class is specified, then create area for displaying preview
		 */
		onOpen: function() {
			const {popupWrappperCss, previewWidgetClass, _popupWrapper, _previewNode} = this;

			if (popupWrappperCss) {
				domClass.add(_popupWrapper, popupWrappperCss);
			}

			// Sticky Actions ("Go to Dialog" and others)
			html.clear(this._actionsNode, true);
			if (this._actionsItems?.length) {
				// create action node
				if (!this._actionsNode) {
					this._actionsNode = html.create("div", {
						"class": "csi-popup-wrapper-actions",
						onclick: (e) => {
							if (!this.ref?.bClickNewAction) {
								// the new action is taken care of by onclickNewAction in _ComboBoxCheckedMenu
								this.onClick(e.target);
							}
						},
					}, _popupWrapper);
				}
				// move action menu items from scrollable menu to action node
				this._actionsItems.forEach((item) => {
					this._actionsNode.appendChild(item);
				})
				const separator = html.create("div", {
					"class": "csi-sticky-actions-separator",
				});
				this._actionsNode.insertBefore(separator, this._actionsNode.firstChild);
				// reset actionsItems array for future uses
				this._actionsItems.length = 0;
			}

			if (previewWidgetClass && !_previewNode) {
				// create preview node
				this._previewNode = html.create("div", {
					"class": "csi-popup-wrapper-preview"
				}, _popupWrapper);
			}
		},

		/**
		 * @override
		 * Ignore disabled items, and show preview if so configured
		 */
		onSelect: function(node) {
			if (!_isDisabledSelection(node)) {
				this.inherited(arguments);
			}
		},

		selectFirstNode: function() {
			// summary:
			//		Select the first displayed item in the list.
			var first = this.containerNode.firstChild;
			while (_isDisabledNode(first)) {
				first = first.nextSibling;
			}
			this._setSelectedAttr(first, true);
		},

		selectLastNode: function() {
			// summary:
			//		Select the last displayed item in the list
			var last = this.containerNode.lastChild;
			while (_isDisabledNode(last)) {
				last = last.previousSibling;
			}
			this._setSelectedAttr(last, true);
		},

		selectNextNode: function() {
			// summary:
			//		Select the item just below the current selection.
			//		If nothing selected, select first node.
			var selectedNode = this.selected;
			if (!selectedNode) {
				this.selectFirstNode();
			} else {
				var next = selectedNode.nextSibling;
				while (_isDisabledNode(next)) {
					next = next.nextSibling;
				}
				if (!next) {
					this.selectFirstNode();
				} else {
					this._setSelectedAttr(next, true);
				}
			}
		},

		selectPreviousNode: function() {
			// summary:
			//		Select the item just above the current selection.
			//		If nothing selected, select last node (if
			//		you select Previous and try to keep scrolling up the list).
			var selectedNode = this.selected;
			if (!selectedNode) {
				this.selectLastNode();
			} else {
				var prev = selectedNode.previousSibling;
				while (_isDisabledNode(prev)) {
					prev = prev.previousSibling;
				}
				if (!prev) {
					this.selectLastNode();
				} else {
					this._setSelectedAttr(prev, true);
				}
			}
		},

		/** 
		 * Override this function from dijit/form/_ListBase 
		 * to fix C8-50058 where actionNode (position: sticky) mess up with winUtils.scrollIntoView(node) 
		 * the only difference is call to html.scrollIntoViewVertically(...) instead of call to winUtils.scrollIntoView(...) 
		 */
		_setSelectedAttr: function(/*DomNode*/ node, /*Boolean*/ scroll) {
			// summary:
			//		Does the actual select.
			// node:
			//		The option to select
			// scroll:
			//		If necessary, scroll node into view.  Set to false for mouse/touch to
			//		avoid jumping problems on mobile/RTL, see https://bugs.dojotoolkit.org/ticket/17739.
			if (this.selected != node) {
				var selectedNode = this.selected;
				if (selectedNode) {
					this.onDeselect(selectedNode);
				}
				if (node) {
					if (scroll) {
						// consider possible margin for actions node when perform scrolling
						const { parentNode } = node;
						// C8-75773, C8-50058 we have different use cases, while using keyboard for scrolling: 
						// parentNode might be scrolled (select custom view dropdown) or parentNode.parentNode might be scrollable (most other cases)
						const scrollNode = html.isScrollingVertically(parentNode) ? parentNode : parentNode.parentNode;
						html.scrollIntoViewVertically(scrollNode, node, this._actionsNode?.offsetHeight || 0);
					}
					this.onSelect(node);
				}
				this._set("selected", node);
			} else if (node) {
				this.onSelect(node);
			}
		},

		_isDisabledSelection,

		/**
		 * create delayed search placeholder (if not already created)
		 * @returns {boolean} - return true if dropdown was created
		 * if we return true, then drop down will be re-opened to recalculate placement
		 * called from _HasDropDownMixin.js->openDropDown()
		 */
		createDelayedPlaceholder() {
			const {delayedStore, placeholderNode, onPlaceholderParams, _popupWrapper} = this;

			if (_popupWrapper && delayedStore && !placeholderNode) {
				// create placeholder node if delayed requests
				const params = onPlaceholderParams();
				this.placeholderNode = new ComboBoxPlaceholder(params).placeAt(_popupWrapper, "first");

				// set initial state and listen for any changes to state
				this._onChangePlaceholderState(delayedStore.state);
				delayedStore.onChangeState = (state) => this._onChangePlaceholderState(state);

				return true;
			}
			return false;
		},

		/** create delayed filtering placeholder (if not already created) */
		createDelayFilteringPlaceholder() {
			const {placeholderNode, _popupWrapper, delayedFilteringStore} = this;

			if (_popupWrapper && !placeholderNode && delayedFilteringStore?.state === 4) {
				this.placeholderNode = new DelayFilteringPlaceholder().placeAt(_popupWrapper, "first");
			}

			if (delayedFilteringStore?.state === 2) {
				// hide the placeholder when switching to the normal mode
				html.hide(this.placeholderNode?.domNode);
			}

			if (this.ref?._resultStore.hideAll) {
				// in the case of empty recent values, hide the container node as per design
				domStyle.set(this.containerNode, "padding", "0");
			}
		},

		/**
		 * React to change in placeholder state
		 * @param {DelayedRequestState} state 
		 */
		_onChangePlaceholderState(state) {
			const {domNode, placeholderNode} = this;
			let res = "";
			let searching = false;
			let results = false;

			switch (state) {
				case DelayedRequestState.WAITING:
					results = true;
					res = "TypeToSeeResults";
					break;
				case DelayedRequestState.SEARCHING:
					res = "Searching";
					searching = true;
					break;
				case DelayedRequestState.NO_RESULTS:
					res = "SEARCH_NO_RESULTS";
					break;
				case DelayedRequestState.RESULTS:
					results = true;
					break;
				default: return;
			}

			placeholderNode.set("message", res ? locale.resource(res) : "");
			placeholderNode.set("searching", searching);
			domNode.classList.toggle("csi-combo-box-no-results", !results);
			html.setVisible(this._actionsNode, !!results);

			if (!results) {
				// if going from having results to no results, then need to clear existing results;
				// cannot use _showResultsList to reopen/resize/position popup as it will also abort
				// the current query, so instead have to manually reset styling so popup fits content
				this.clearResultList();
				html.setVisible(this.nextButton, false);
				html.setVisible(this.previousButton, false);
				this._popupWrapper.style.height = "";
			}
		},

		/** 
		 * this function is copy-pasted from dijit/form/_ComboBoxMenu as it has bug 
		 * causing endless loop when user use pgUp in dropdowns with grouping 
		 * function is copied as is, our changes are marked as "Centric changes"
		 * committed as part of C8-75773
		 */
		_page: function(/*Boolean*/ up){
			// summary:
			//		Handles page-up and page-down keypresses

			var scrollamount = 0;
			var oldscroll = this.domNode.scrollTop;
			var height = domStyle.get(this.domNode, "height");
			// if no item is highlighted, highlight the first option
			if(!this.getHighlightedOption()){
				this.selectNextNode();
			}
			while( (scrollamount < height)){
				var highlighted_option = this.getHighlightedOption();
				if(up){
					// Start Centric changes
					const prev = highlighted_option.previousSibling;

					// stop at option 1
					if(!prev || (prev.style.display == "none")){
						break;
					}

					// check if first visible option is group name and then stop searching and then stop search 
					// and so prevent to loop back to last option which is causing endless loop
					if (prev.classList.contains("csi-disabled-selection") && (prev.previousSibling.style.display == "none")) {
						this.domNode.scrollTop = 0;
						break;
					}
					// end of Centric Changes

					this.selectPreviousNode();
				}else{
					// stop at last option
					if(!highlighted_option.nextSibling ||
						highlighted_option.nextSibling.style.display == "none"){
						break;
					}
					this.selectNextNode();
				}
				// going backwards
				var newscroll = this.domNode.scrollTop;
				scrollamount += (newscroll-oldscroll)*(up ? -1:1);
				oldscroll = newscroll;
			}
		},
	});
});